/**
 * PipelinePage (SPEC E3 - pipeline-and-capi, R2, AMENDED build).
 *
 * A kanban board of opportunities grouped by E6 pipeline stage. Columns,
 * labels, colors, and ordinals come from GET /api/pipeline/definitions; counts /
 * total / weighted / stale come from GET /api/pipeline/summary; per-column deals
 * from GET /api/opportunities?stage_key=. Dragging a card to another column
 * calls POST /api/pipeline/move (optimistic, reverts on error). A pipeline
 * selector (E3-R2 multi-pipeline) and a filter bar (E3-M1) sit in the header;
 * clicking a card opens the deal-detail side panel (E3-H4).
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { KanbanSquare } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { ListSkeleton } from '@/components/ListSkeleton';
import { KanbanColumn } from '@/components/pipeline/KanbanColumn';
import {
  PipelineFilters,
  applyFilters,
} from '@/components/pipeline/PipelineFilters';
import { DealDetailPanel } from '@/components/pipeline/DealDetailPanel';
import {
  getPipelineSummary,
  getPipelineDefinition,
  listPipelines,
  listOpportunities,
  moveOpportunities,
} from '@/lib/api/pipeline';
import type {
  Opportunity,
  PipelineDefinition,
  PipelineSummary,
  KanbanFilters,
} from '@/types/pipeline';

export function PipelinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pipelineId = searchParams.get('pipeline_id') || undefined;

  const [pipelines, setPipelines] = useState<PipelineDefinition[]>([]);
  const [definition, setDefinition] = useState<PipelineDefinition | null>(null);
  const [summary, setSummary] = useState<PipelineSummary | null>(null);
  // stage_key -> opportunities in that column.
  const [deals, setDeals] = useState<Record<string, Opportunity[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Opportunity | null>(null);
  // R3c multi-select: ids of cards selected for a bulk move.
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());

  const filters: KanbanFilters = useMemo(
    () => ({
      owner: searchParams.get('owner') || undefined,
      createdAfter: searchParams.get('created_after') || undefined,
      createdBefore: searchParams.get('created_before') || undefined,
      minValue: searchParams.get('min_value')
        ? Number(searchParams.get('min_value'))
        : undefined,
      maxValue: searchParams.get('max_value')
        ? Number(searchParams.get('max_value'))
        : undefined,
    }),
    [searchParams]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [def, sum, pipes] = await Promise.all([
        getPipelineDefinition(pipelineId),
        getPipelineSummary(pipelineId),
        listPipelines(),
      ]);
      setDefinition(def);
      setSummary(sum);
      setPipelines(pipes);

      // Fetch each stage's deals in parallel.
      const stageEntries = await Promise.all(
        def.stages.map(async (stage) => {
          const res = await listOpportunities({
            stageKey: stage.stage_key,
            pipelineId: def.pipeline_id,
            limit: 200,
          });
          return [stage.stage_key, res.opportunities] as const;
        })
      );
      setDeals(Object.fromEntries(stageEntries));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const updateFilters = (next: KanbanFilters) => {
    const params = new URLSearchParams(searchParams);
    const apply = (key: string, val?: string | number) => {
      if (val === undefined || val === '' || val === null) params.delete(key);
      else params.set(key, String(val));
    };
    apply('owner', next.owner);
    apply('created_after', next.createdAfter);
    apply('created_before', next.createdBefore);
    apply('min_value', next.minValue);
    apply('max_value', next.maxValue);
    setSearchParams(params, { replace: true });
  };

  const selectPipeline = (pid: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('pipeline_id', pid);
    setSearchParams(params, { replace: true });
  };

  const [, setActiveId] = useState<string | null>(null);
  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const onDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const opp = active.data.current?.opportunity as Opportunity | undefined;
    const targetStage = String(over.id);
    if (!opp || opp.stage_key === targetStage) return;

    const sourceStage = opp.stage_key;

    // Optimistic move.
    setDeals((prev) => {
      const next = { ...prev };
      next[sourceStage] = (next[sourceStage] || []).filter((d) => d.id !== opp.id);
      next[targetStage] = [
        { ...opp, stage_key: targetStage },
        ...(next[targetStage] || []),
      ];
      return next;
    });

    try {
      const res = await moveOpportunities({
        opportunityIds: [opp.id],
        stageKey: targetStage,
        pipelineId: definition?.pipeline_id,
      });
      if (res.moved < 1) {
        // Gated (required-field) or skipped - revert + inform.
        throw new Error(
          res.gated > 0
            ? 'Move blocked: target stage requires a deal value'
            : 'Move was skipped'
        );
      }
      // Refresh summary counts (best-effort).
      getPipelineSummary(definition?.pipeline_id)
        .then(setSummary)
        .catch(() => undefined);
    } catch (err) {
      // Revert optimistic move.
      setDeals((prev) => {
        const next = { ...prev };
        next[targetStage] = (next[targetStage] || []).filter(
          (d) => d.id !== opp.id
        );
        next[sourceStage] = [opp, ...(next[sourceStage] || [])];
        return next;
      });
      toast.error(err instanceof Error ? err.message : 'Failed to move deal');
    }
  };

  const onDealSaved = (updated: Opportunity) => {
    setDeals((prev) => {
      const next = { ...prev };
      const list = next[updated.stage_key] || [];
      next[updated.stage_key] = list.map((d) =>
        d.id === updated.id ? { ...d, ...updated } : d
      );
      return next;
    });
    setSelected(null);
  };

  const stages = summary?.stages || [];
  const currency = summary?.currency || 'CAD';

  // R3c bulk-select helpers. Bulk move uses the EXISTING array-capable
  // moveOpportunities endpoint in ONE call - no new endpoint, no widened access.
  const toggleCardSelect = useCallback((id: string, checked: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const clearBulkSelection = useCallback(() => setBulkSelectedIds(new Set()), []);

  const handleBulkMove = useCallback(
    async (targetStage: string) => {
      const ids = Array.from(bulkSelectedIds);
      if (ids.length === 0 || !targetStage) return;
      try {
        const res = await moveOpportunities({
          opportunityIds: ids,
          stageKey: targetStage,
          pipelineId: definition?.pipeline_id,
        });
        toast.success(`Moved ${res.moved} deal${res.moved === 1 ? '' : 's'}`);
        if (res.gated > 0) {
          toast.warning(`${res.gated} blocked (target stage requires a deal value)`);
        }
        clearBulkSelection();
        // Reload the board so the columns reflect the server state.
        await loadBoard();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Bulk move failed');
      }
    },
    [bulkSelectedIds, definition?.pipeline_id, clearBulkSelection, loadBoard]
  );

  return (
    <div className="flex flex-col h-full p-6 gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Eyebrow>Deals</Eyebrow>
          <h1 className="text-2xl font-heading font-bold tracking-tight">Pipeline</h1>
          <p className="text-sm text-muted-foreground">
            {summary
              ? `${summary.open_count} open · $${summary.total_value.toLocaleString(
                  'en-CA'
                )} ${currency} · weighted $${summary.weighted_total.toLocaleString(
                  'en-CA'
                )} ${currency}`
              : 'Drag deals between stages to advance them.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {pipelines.length > 0 && (
            <Select
              value={definition?.pipeline_id}
              onValueChange={selectPipeline}
            >
              <SelectTrigger className="w-48" data-testid="pipeline-selector">
                <SelectValue placeholder="Select pipeline" />
              </SelectTrigger>
              <SelectContent>
                {pipelines.map((p) => (
                  <SelectItem key={p.pipeline_id} value={p.pipeline_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </header>

      <PipelineFilters filters={filters} onChange={updateFilters} />

      {/* R3c bulk-action bar - appears on selection; bulk move calls the
          existing array-capable moveOpportunities endpoint. */}
      {bulkSelectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3 shadow-sm"
          data-testid="pipeline-bulk-bar"
        >
          <span className="text-sm font-medium">{bulkSelectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <Select onValueChange={handleBulkMove}>
              <SelectTrigger className="h-9 w-[200px]">
                <SelectValue placeholder="Move to stage…" />
              </SelectTrigger>
              <SelectContent>
                {stages.map((s) => (
                  <SelectItem key={s.stage_key} value={s.stage_key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={clearBulkSelection}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {error ? (
        <ErrorState
          message={error}
          onRetry={() => window.location.reload()}
        />
      ) : stages.length === 0 && loading ? (
        // Skeleton on initial load (was a bare "Loading pipeline…" string).
        <ListSkeleton rows={4} className="flex-1" />
      ) : !loading && summary && summary.open_count === 0 ? (
        // Guided empty state when the pipeline has no open deals.
        <EmptyState
          icon={KanbanSquare}
          title="No deals in your pipeline yet"
          description="Deals appear here as leads progress. Add prospects or move a warm lead into the pipeline to get started."
        />
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div className="flex-1 overflow-x-auto">
            <div className="flex gap-3 min-h-[200px] pb-4">
              {stages.map((stage) => (
                <KanbanColumn
                  key={stage.stage_key}
                  stage={stage}
                  currency={currency}
                  loading={loading}
                  opportunities={applyFilters(
                    deals[stage.stage_key] || [],
                    filters
                  )}
                  onCardClick={setSelected}
                  selectedIds={bulkSelectedIds}
                  onCardSelectChange={toggleCardSelect}
                />
              ))}
            </div>
          </div>
        </DndContext>
      )}

      <DealDetailPanel
        opportunity={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onSaved={onDealSaved}
      />
    </div>
  );
}

export default PipelinePage;
